import React, { useEffect, useState, useCallback } from "react";
import { ArrowLeft, RefreshCw, Download, Gauge } from "lucide-react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import wqmsService from "../services/wqmsService";
import "../styles/exceedance.css";

/* ---------------------- CSV helpers ---------------------- */
const csvEscape = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const buildCSV = (rows, headers) => {
  const head = headers.map(csvEscape).join(",");
  const body = rows
    .map((r) => {
      const shaped = { ...r };
      if ("First Exceedance" in shaped)
        shaped["First Exceedance"] = fmtDate(shaped["First Exceedance"]);
      if ("Last Exceedance" in shaped)
        shaped["Last Exceedance"] = fmtDate(shaped["Last Exceedance"]);
      return headers.map((h) => csvEscape(shaped[h])).join(",");
    })
    .join("\n");
  return head + "\n" + body + "\n";
};

const downloadCSV = (csvText, filename) => {
  const BOM = "\uFEFF";
  const blob = new Blob([BOM + csvText], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const safeForFile = (s) => s.replace(/[^A-Za-z0-9._ -]+/g, "_");
const stationNameOf = (stations, id) =>
  (stations.find((s) => String(s.id) === String(id))?.name) || `Station ${id}`;

/* ---------------------- Helpers ---------------------- */
function normalizeStations(raw) {
  const arr = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.data)
    ? raw.data
    : Array.isArray(raw?.rows)
    ? raw.rows
    : Array.isArray(raw?.stations)
    ? raw.stations
    : [];
  return arr.map((s, i) => {
    const idRaw = s.id ?? s.station_id ?? s.stationId ?? (i + 1);
    const nameRaw =
      s.name ??
      s.site_name ??
      s.station_name ??
      (s.district ? `Station ${idRaw} — ${s.district}` : `Station ${idRaw}`);
    return { id: String(idRaw), name: String(nameRaw) };
  });
}

function decideRes(startISO, endISO) {
  const diff = dayjs(endISO).diff(dayjs(startISO), "day", true);
  if (diff <= 1) return "daily";
  if (diff <= 31) return "weekly";
  return "monthly";
}

// Pretty label for resolution
const prettyRes = (r) => (r ? r.charAt(0).toUpperCase() + r.slice(1) : "");
// DB already in PKT; format only
const fmtDate = (s) => (s ? dayjs(s).format("YYYY-MM-DD HH:mm:ss") : "");

// Map backend keys -> professional display labels
const PARAM_LABELS = {
  cod_mg_l_clean: "COD (mg/L)",
  bod_mg_l_clean: "BOD (mg/L)",
  dissolved_oxygen_mg_l_clean: "Dissolved Oxygen (mg/L)",
  ph_clean: "pH",
};
const paramLabel = (k) => PARAM_LABELS[k] || k;

/* ---------------------- Component ---------------------- */
export default function ExceedanceReport() {
  const navigate = useNavigate();

  // filters/data
  const [stations, setStations] = useState([]);
  const [stationId, setStationId] = useState("");
  const [rows, setRows] = useState([]);
  const [start, setStart] = useState(dayjs().subtract(7, "day").format("YYYY-MM-DD"));
  const [end, setEnd] = useState(dayjs().format("YYYY-MM-DD"));
  const [res, setRes] = useState("daily");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // pagination
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [stationId, start, end, rows]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const startIdx = (page - 1) * PAGE_SIZE;
  const endIdx = Math.min(rows.length, startIdx + PAGE_SIZE);
  const pageRows = rows.slice(startIdx, endIdx);

  useEffect(() => {
    (async () => {
      try {
        const list = await wqmsService.getStations({ timeRange: "10", force: true });
        const norm = normalizeStations(list);
        setStations(norm);
        if (!stationId && norm.length) setStationId(String(norm[0].id));
      } catch (e) {
        console.error(e);
        setStations([]);
      }
    })();
  }, []); // mount

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const effectiveRes = decideRes(start, end);
      setRes(effectiveRes);

      const data = await wqmsService.getExceedanceReport({
        start,
        end,
        res: effectiveRes,
        stationId,
      });

      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || "Failed to load exceedance data");
    } finally {
      setLoading(false);
    }
  }, [stationId, start, end]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onExportCSV = () => {
    if (!rows.length) return;

    const prettyRows = rows.map((r) => ({
      ...r,
      Station: stationNameOf(stations, r["Station"]),
      Parameter: paramLabel(r["Parameter"]),
      "First Exceedance": fmtDate(r["First Exceedance"]),
      "Last Exceedance": fmtDate(r["Last Exceedance"]),
    }));

    const headers = [
      "Station",
      "Parameter",
      "Limit",
      "Max",
      "Avg",
      "Count of Exceedances",
      "% Time Over",
      "First Exceedance",
      "Last Exceedance",
      "Notes",
    ].filter((h) => h in prettyRows[0]);

    const csv = buildCSV(prettyRows, headers);
    const stName = stationId ? stationNameOf(stations, stationId) : "AllStations";
    const fname = `${safeForFile(stName)}_Exceedance_${start}_to_${end}.csv`;
    downloadCSV(csv, fname);
  };

  return (
    <div className="twoy-wrap">
      <div className="admin-page-header">
        <div className="admin-header-row">
          <div className="admin-header-back">
            <button type="button" className="admin-btn admin-btn-secondary" onClick={() => navigate(-1)}>
              <ArrowLeft size={16} />
              Back
            </button>
          </div>
          <div className="admin-header-main">
            <h1 className="admin-page-title">
              <Gauge size={22} /> Regulatory Exceedance Summary
            </h1>
            <p className="admin-page-subtitle">
              Shows parameters that crossed regulatory limits by station and duration.
            </p>
            <div className="admin-header-chips">
              <span className="twoy-chip">
                {dayjs(start).format("YYYY-MM-DD")} → {dayjs(end).format("YYYY-MM-DD")}
              </span>
            </div>
          </div>
          <div className="admin-header-actions">
            <button type="button" className="admin-btn admin-btn-primary" onClick={fetchData} disabled={loading}>
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="twoy-controls">
        <div className="twoy-field">
          <label>Station</label>
          <select value={stationId} onChange={(e) => setStationId(e.target.value)}>
            <option value="">All Stations</option>
            {stations.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        <div className="twoy-field">
          <label>Start (PKT)</label>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
        </div>

        <div className="twoy-field">
          <label>End (PKT)</label>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
        </div>
      </div>

      {/* Table */}
      <div className="exceedance-card">
        <div className="exceedance-toolbar">
          <span className="ex-chip">Resolution: {prettyRes(res)}</span>
          <span className="ex-helper">Showing exceedances by station &amp; parameter</span>
        </div>

        {rows.length === 0 ? (
          <div className="ex-empty">No exceedances in selected period.</div>
        ) : (
          <div className="ex-table-wrap">
            <table className="ex-table">
              <thead>
                <tr>
                  <th>Station</th>
                  <th>Parameter</th>
                  <th>Limit</th>
                  <th className="ex-num">Max</th>
                  <th className="ex-num">Avg</th>
                  <th className="ex-num">Count</th>
                  <th className="ex-num">% Time Over</th>
                  <th>First Exceedance</th>
                  <th>Last Exceedance</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r, i) => {
                  const pct = Number(r["% Time Over"] ?? 0);
                  const note = r["Notes"] || "";
                  const badgeClass = note.includes("Within") ? "ok" : pct >= 50 ? "bad" : "warn";

                  return (
                    <tr key={`${startIdx + i}-${r["Station"]}-${r["Parameter"]}`}>
                      <td className="ex-cell">{stationNameOf(stations, r["Station"])}</td>
                      <td className="ex-cell">{paramLabel(r["Parameter"])}</td>
                      <td className="ex-cell">{r["Limit"]}</td>
                      <td className="ex-num">{r["Max"]}</td>
                      <td className="ex-num">{r["Avg"]}</td>
                      <td className="ex-num">{r["Count of Exceedances"]}</td>
                      <td className="ex-num">{r["% Time Over"]}</td>
                      <td className="ex-cell">{fmtDate(r["First Exceedance"])}</td>
                      <td className="ex-cell">{fmtDate(r["Last Exceedance"])}</td>
                      <td className="ex-cell">
                        <span className={`badge ${badgeClass}`}>{note}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pager */}
            <div className="ex-pager">
              <div className="ex-pager-info">
                Showing <strong>{rows.length ? startIdx + 1 : 0}</strong>–<strong>{endIdx}</strong> of{" "}
                <strong>{rows.length}</strong>
              </div>
              <div className="ex-pager-buttons">
                <button className="btn btn-ghost" onClick={() => setPage(1)} disabled={page === 1}>
                  « First
                </button>
                <button
                  className="btn btn-ghost"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  ‹ Prev
                </button>
                <span className="ex-page-chip">Page {page} / {totalPages}</span>
                <button
                  className="btn btn-ghost"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next ›
                </button>
                <button
                  className="btn btn-ghost"
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages}
                >
                  Last »
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
